const API_BASE = '/api/v1';

interface RequestOptions extends RequestInit {
  params?: Record<string, string>;
}

class ApiClient {
  private keyId: string | null = null;

  setKeyId(keyId: string) {
    this.keyId = keyId;
  }

  clearKeyId() {
    this.keyId = null;
  }

  private async request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const { params, ...fetchOptions } = options;

    let url = `${API_BASE}${endpoint}`;
    if (params) {
      const searchParams = new URLSearchParams(params);
      url += `?${searchParams.toString()}`;
    }

    const headers: HeadersInit = {
      ...fetchOptions.headers,
    };

    if (this.keyId && !(headers as Record<string, string>)['X-Key-Id']) {
      (headers as Record<string, string>)['X-Key-Id'] = this.keyId;
    }

    if (fetchOptions.body && typeof fetchOptions.body === 'string') {
      (headers as Record<string, string>)['Content-Type'] = 'application/json';
    }

    const response = await fetch(url, {
      ...fetchOptions,
      headers,
    });

    if (!response.ok) {
      if (response.status === 401) {
        this.clearKeyId();
        throw new Error('Unauthorized');
      }
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    if (response.status === 204) {
      return undefined as T;
    }

    const contentType = response.headers.get('content-type');
    if (contentType?.includes('application/json')) {
      return response.json();
    }

    return response.blob() as Promise<T>;
  }

  async get<T>(endpoint: string, options?: { params?: Record<string, string>; headers?: Record<string, string> }): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET', params: options?.params, headers: options?.headers });
  }

  async post<T>(endpoint: string, data?: unknown, options?: { headers?: Record<string, string> }): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
      headers: options?.headers,
    });
  }

  async put<T>(endpoint: string, data?: unknown, options?: { headers?: Record<string, string> }): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
      headers: options?.headers,
    });
  }

  async delete<T>(endpoint: string, options?: { headers?: Record<string, string> }): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE', headers: options?.headers });
  }

  async uploadFile<T>(endpoint: string, formData: FormData): Promise<T> {
    const headers: HeadersInit = {};
    if (this.keyId && !(headers as Record<string, string>)['X-Key-Id']) {
      (headers as Record<string, string>)['X-Key-Id'] = this.keyId;
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.status}`);
    }

    return response.json();
  }

  async downloadFile(endpoint: string): Promise<Blob> {
    return this.request<Blob>(endpoint, { method: 'GET' });
  }

  async getText(endpoint: string, params?: Record<string, string>): Promise<string> {
    let url = `${API_BASE}${endpoint}`;
    if (params) {
      const searchParams = new URLSearchParams(params);
      url += `?${searchParams.toString()}`;
    }

    const headers: HeadersInit = {};
    if (this.keyId && !(headers as Record<string, string>)['X-Key-Id']) {
      (headers as Record<string, string>)['X-Key-Id'] = this.keyId;
    }

    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      if (response.status === 401) {
        this.clearKeyId();
        throw new Error('Unauthorized');
      }
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return response.text();
  }
}

export const apiClient = new ApiClient();
